import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { Plus, MessageSquare, X, UserX, LogOut, LogIn } from "lucide-react";
import { isNative } from "../lib/nativePlatform";
import { startGoogleLogin } from "../lib/googleAuth";
import { useAuth } from "../lib/auth";

/**
 * Iter XX.21 — ChatGPT-style conversation sidebar.
 *
 * Left drawer with:
 *   · "+ New Chat" button (spawns a fresh thread, preserves long-term memory).
 *   · Scrollable list of the player's past threads.
 *   · Tap a thread → parent switches to that session_id (instant).
 *
 * Long-term memory (identity, journey, reports, patterns, season review)
 * lives on `user_id` server-side and follows the player across threads.
 * Never touched here — this component ONLY manages the `session_id`.
 */
export default function ConversationsSidebar({
  currentSessionId,
  onSwitchSession,
  onClose,
  open,
}) {
  const [sessions, setSessions] = useState([]);
  // Card 4 regression — initial `loading` MUST be `false`, not `true`.
  // We only flip it to `true` inside `load()` right before the actual
  // fetch. The old `useState(true)` combined with the `!currentSessionId`
  // short-circuit produced a permanently-stuck "Loading threads…" state:
  // if the drawer opened before Chat.jsx had hydrated `sessionId`
  // (specifically the sign-out → Google-sign-in → AuthCallback → Chat
  // remount cycle), the effect ran, load() early-returned WITHOUT
  // touching loading, and the initial `true` persisted forever. Even a
  // full page reload did not recover because a subsequent load() with
  // a valid session id only set `loading = true → false`, leaving the
  // stale initial paint of "Loading threads…" visible until the fetch
  // committed. Starting at `false` means the "Loading threads…" row
  // only appears while a real fetch is in flight.
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  // XX.293b — reuse the app-wide logout handler so a user can sign
  // out from the /chat sidebar. `logout()` (from `lib/auth.jsx`)
  // already does the full XX.291 client cleanup (token, session_id,
  // 5 identity-adjacent keys) and POSTs `/api/auth/logout` which
  // now emits the XX.293 attribute-matched delete-cookie header.
  //
  // Card 4 regression fix — read `user` + `loading` from the
  // AuthProvider. The ACCOUNT area MUST gate on the authoritative
  // `user` state (post-`/auth/me` resolution) — NOT on `hasToken()`:
  //   · Password / phone auth writes `gully_token` to localStorage
  //     via `setToken()`. Google OAuth (Iter 54) does NOT — it
  //     stores an HTTP-only session cookie and populates `user`
  //     via `AuthCallback` → `setUser(data.user)`.
  //   · `hasToken()` is not React state — reading it in JSX never
  //     triggers a re-render when localStorage flips. Reading `user`
  //     from `useAuth()` subscribes this component to auth changes
  //     so the ACCOUNT area actually flips on login/logout.
  //   · The earlier `!!user || hasToken()` fallback caused a
  //     regression where a stale `gully_token` (e.g. a password-auth
  //     session that pre-dates the Card 4 sign-out control) made the
  //     signed-in section render — including "Delete Account" — for
  //     a user whose `/auth/me` had returned 401. Dropping the
  //     fallback fixes that. The `!authLoading` gate below already
  //     prevents a flash-of-wrong-control on cold boot, so the
  //     fallback was never actually load-bearing.
  const { logout: doLogout, user, loading: authLoading } = useAuth();
  const authenticated = !!user;
  const [signingOut, setSigningOut] = useState(false);

  const load = useCallback(async () => {
    // Card 4 regression — when we can't fetch (no session id yet),
    // explicitly clear the loading flag so the sidebar renders the
    // empty-state row instead of the stuck "Loading threads…" row.
    // The effect below will re-fire when `currentSessionId` (or
    // `user`) changes, at which point `load()` runs to completion.
    if (!currentSessionId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/web/sessions", {
        params: { session_id: currentSessionId, limit: 50 },
      });
      setSessions(data.sessions || []);
    } catch {
      /* silent — sidebar is best-effort UX */
    } finally { setLoading(false); }
  }, [currentSessionId]);

  // Card 4 regression — re-fetch when the AUTH STATE flips too, not
  // just when `open` toggles. The exact failure: sign-out → new
  // anonymous session created → Google sign-in → server-side
  // `_runClaim` stitches the anonymous session to the returning user
  // AFTER Chat.jsx has mounted with `sessionId = <old anonymous sid>`.
  // Without `user` in the dep list, the sidebar's first fetch (which
  // may have raced ahead of the claim and returned only the current
  // session) never re-runs, so the drawer shows one empty thread
  // instead of the user's ~26 restored threads. Adding `user` here
  // guarantees the sidebar re-queries `/web/sessions` the instant
  // `user` transitions from null → set (i.e. `_runClaim` has landed
  // and `checkAuth` has resolved).
  useEffect(() => { if (open) load(); }, [open, load, user]);

  async function handleNewChat() {
    if (creating || !currentSessionId) return;
    setCreating(true);
    try {
      const { data } = await api.post("/web/session/new", null, {
        params: { session_id: currentSessionId },
      });
      if (data?.session_id) {
        onSwitchSession(data.session_id, /* isNew */ true);
      }
    } catch {
      /* silent */
    } finally { setCreating(false); }
  }

  function handlePick(sid) {
    if (sid === currentSessionId) { onClose?.(); return; }
    onSwitchSession(sid, /* isNew */ false);
  }

  return (
    <>
      {/* Backdrop — click to dismiss on mobile/tablet */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:bg-black/40 backdrop-blur-sm"
          onClick={onClose}
          data-testid="conv-sidebar-backdrop"
          aria-hidden
        />
      )}
      <aside
        className={`fixed z-50 top-0 left-0 h-full w-[280px] bg-[#111] border-r border-neutral-800
                    transform transition-transform duration-200 ease-out flex flex-col pb-mobile-nav
                    ${open ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="conv-sidebar"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-800">
          <p className="text-sm font-medium text-neutral-100">Conversations</p>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded text-neutral-400 hover:text-neutral-100 hover:bg-neutral-800/70"
            aria-label="Close sidebar"
            data-testid="conv-sidebar-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-3 pt-3">
          <button
            type="button"
            onClick={handleNewChat}
            disabled={creating || !currentSessionId}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-800/50 bg-emerald-950/40 hover:bg-emerald-900/60 text-emerald-100 text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="conv-sidebar-new-chat"
          >
            <Plus size={16} />
            {creating ? "Starting…" : "New chat"}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-2 py-3 space-y-1">
          {loading && <div className="px-3 py-2 text-xs text-neutral-500">Loading threads…</div>}
          {!loading && sessions.length === 0 && (
            <div className="px-3 py-2 text-xs text-neutral-500" data-testid="conv-sidebar-empty">
              No previous threads yet.
            </div>
          )}
          {sessions.map((s) => (
            <button
              key={s.session_id}
              onClick={() => handlePick(s.session_id)}
              className={`w-full text-left px-3 py-2 rounded-md flex items-start gap-2 transition-colors ${
                s.is_current
                  ? "bg-neutral-800 text-neutral-100"
                  : "hover:bg-neutral-900 text-neutral-300"
              }`}
              data-testid={`conv-sidebar-thread-${s.session_id}`}
            >
              <MessageSquare size={14} className="mt-0.5 flex-none text-neutral-500" />
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{s.title}</p>
                <p className="text-[10px] text-neutral-500 tabular-nums mt-0.5">
                  {s.user_msg_count} messages
                </p>
              </div>
            </button>
          ))}
        </div>
        <div className="px-4 py-2 border-t border-neutral-800 text-[10px] text-neutral-600">
          Long-term memory follows you across threads.
        </div>

        {/* Iter XX.227 — Account section (Apple review discoverability).
            Signed-in only: exposes the existing /account/delete route via
            the drawer so App Store reviewers can find deletion in three
            taps: hamburger → drawer → Delete Account. Guests never see
            this row (nothing to delete). No new deletion logic — reuses
            the existing AccountDelete.jsx page and backend flow.
            XX.293b — Sign out lives here too: reuses AuthProvider
            logout (XX.291 client clear + XX.293 cookie clear). After
            logout we hard-navigate to /chat so the next render is a
            genuinely fresh anonymous session. */}
        {/* Card 4 regression fix — gate on `authenticated` (user OR
            token) instead of `hasToken()` alone so Google-authenticated
            users (session cookie only, no localStorage token) see the
            correct ACCOUNT state. While the initial `/auth/me` probe
            is in flight we render nothing here — better a blank
            ACCOUNT strip for one tick than flashing the wrong
            control. */}
        {!authLoading && authenticated && (
          <div
            className="px-3 py-3 border-t border-neutral-800"
            data-testid="conv-sidebar-account-section"
          >
            <p className="px-1 pb-2 text-[10px] uppercase tracking-wider text-neutral-500">
              Account
            </p>
            <button
              type="button"
              disabled={signingOut}
              onClick={async () => {
                if (signingOut) return;
                setSigningOut(true);
                try { await doLogout(); } catch { /* fail-open */ }
                // Hard reload so any in-memory state (Chat.jsx thread
                // list, session_id, etc.) is dropped and the next
                // render starts from a genuinely fresh anonymous
                // state — matching the XX.293 acceptance contract.
                try { window.location.assign("/chat"); }
                catch { /* SSR / tests */ }
              }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-neutral-300 hover:bg-neutral-900 hover:text-neutral-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="conv-sidebar-sign-out"
            >
              <LogOut size={14} className="flex-none text-neutral-500" />
              <span>{signingOut ? "Signing out…" : "Sign out"}</span>
            </button>
            <a
              href="/account/delete"
              className="mt-1 w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-neutral-300 hover:bg-neutral-900 hover:text-neutral-100 transition-colors"
              data-testid="conv-sidebar-delete-account"
            >
              <UserX size={14} className="flex-none text-neutral-500" />
              <span>Delete Account</span>
            </a>
          </div>
        )}
        {/* Card 4 — Signed-out Sign in entry point. Mirrors the
            signed-in Sign out row's discoverability so a signed-out
            user (post-logout OR fresh anonymous) can recover their
            existing Gully identity through the Gully-owned Google OAuth
            flow (P0 2026-10-01, no Emergent; reused via
            `lib/googleAuth.js`). Hidden on Capacitor native — parity
            with `Login.jsx` (Iter XX.181): the OAuth flow
            expects an HTTPS-origin redirect, and Safari cannot open
            `capacitor://localhost`. Native shells continue to sign in
            via the existing email/password + magic-link path. */}
        {!authLoading && !authenticated && !isNative() && (
          <div
            className="px-3 py-3 border-t border-neutral-800"
            data-testid="conv-sidebar-signed-out-account-section"
          >
            <p className="px-1 pb-2 text-[10px] uppercase tracking-wider text-neutral-500">
              Account
            </p>
            <button
              type="button"
              onClick={() => {
                // Card 4 — return to /chat so AuthCallback resolves
                // the user back into the existing identity/history
                // path (unchanged Iter 54 behavior).
                try { startGoogleLogin("/chat"); }
                catch { /* redirect can't be recovered locally */ }
              }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-neutral-300 hover:bg-neutral-900 hover:text-neutral-100 transition-colors"
              data-testid="conv-sidebar-sign-in-google"
            >
              <LogIn size={14} className="flex-none text-neutral-500" />
              <span>Sign in with Google</span>
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
